import { AuthLayout } from '@/modules/auth/ui/components'
import { LoadingSpinner } from '@/shared/ui/components'
import { Button } from '@/shared/ui/primitives'
import type { SendOtpWithEmailRedirect } from '@/modules/auth/use-cases'
import { useVerifyEmailPage } from './hooks/use-verify-email-page'

export type VerifyEmailPageProps = {
  onSendOtpWithEmailRedirect: SendOtpWithEmailRedirect
}

export function VerifyEmailPage({ onSendOtpWithEmailRedirect }: VerifyEmailPageProps) {
  const vm = useVerifyEmailPage({ onSendOtpWithEmailRedirect })

  if (vm.status === 'loading') {
    return (
      <AuthLayout
        title="Verify your email"
        subtitle="We're sending you a magic link to finish setup."
      >
        <div
          data-testid="verify-email-loading"
          className="flex flex-col items-center justify-center gap-3 py-6"
        >
          <LoadingSpinner size="lg" />
        </div>
      </AuthLayout>
    )
  }

  if (vm.status === 'error') {
    return (
      <AuthLayout
        title="Verification email failed"
        subtitle="We couldn't send your magic link."
      >
        <div data-testid="verify-email-error" className="flex flex-col gap-4">
          <p role="alert" className="text-sm text-destructive">
            {vm.errorMessage}
          </p>
          <Button
            type="button"
            variant="default"
            className="w-full"
            onClick={vm.handleResend}
            disabled={vm.resendDisabled}
          >
            {vm.resendDisabled ? vm.resendLabel : 'Resend email'}
          </Button>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Check your email"
      subtitle="Click the link in the email to verify your account and complete setup."
    >
      <div data-testid="verify-email-sent" className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">
          We sent a magic link to <span className="font-medium text-foreground">{vm.email}</span>
        </p>
        <Button
          type="button"
          variant="default"
          className="w-full"
          onClick={vm.handleResend}
          disabled={vm.resendDisabled}
        >
          {vm.resendDisabled ? vm.resendLabel : 'Resend email'}
        </Button>
      </div>
    </AuthLayout>
  )
}
